import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';

interface CardDialogProps {
  team: 'home' | 'away';
  onClose: () => void;
  onSubmit: (card: { team: 'home' | 'away'; playerNumber: string; type: 'yellow' | 'red'; reason?: string }) => void;
}

function CardDialog({ team, onClose, onSubmit }: CardDialogProps) {
  const [playerNumber, setPlayerNumber] = useState('');
  const [cardType, setCardType] = useState<'yellow' | 'red'>('yellow');
  const [reason, setReason] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus input when dialog opens
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (playerNumber.trim()) {
      onSubmit({ 
        team, 
        playerNumber: playerNumber.trim(), 
        type: cardType,
        reason: reason.trim() || undefined
      });
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby="card-dialog-description">
        <DialogHeader>
          <DialogTitle>Issue Card - {team === 'home' ? 'Home' : 'Away'} Team</DialogTitle>
        </DialogHeader>
        <p id="card-dialog-description" className="sr-only">
          Enter the player number, select the type of card, and optionally add a reason
        </p>
        <form onSubmit={handleSubmit} className="space-y-4 w-full">
          <div className="space-y-2">
            <Label htmlFor="player-number">Player Number</Label>
            <Input
              ref={inputRef}
              id="player-number"
              type="text"
              placeholder="Enter player number"
              value={playerNumber}
              onChange={(e) => setPlayerNumber(e.target.value)}
              aria-required="true"
              className="w-full max-w-full"
            />
          </div>
          <div className="space-y-2">
            <Label>Card Type</Label>
            <RadioGroup value={cardType} onValueChange={(v) => setCardType(v as 'yellow' | 'red')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yellow" id="yellow" />
                <Label htmlFor="yellow" className="cursor-pointer">🟨 Yellow Card</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="red" id="red" />
                <Label htmlFor="red" className="cursor-pointer">🟥 Red Card</Label>
              </div>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label htmlFor="card-reason">Reason (optional)</Label>
            <Textarea
              id="card-reason"
              placeholder="e.g. Dissent, reckless challenge, time wasting..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[80px] resize-none"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={!playerNumber.trim()}>
              Issue Card
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CardDialog;